import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { OLITASK_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const MEETING_POINTS_API_BASE_URL = `${OLITASK_SERVICE_BASE_URL}/api/v1/travo-task-meeting-points`;
const MEETING_POINTS_TOAST_CONFIG = {
  theme: "colored",
  style: { zIndex: 110000 },
};

const normalizeRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const extractErrorMessage = (err, action = "operation") => {
  const fallback = `Failed to ${action}.`;
  const data = err?.response?.data;
  if (!data) return err?.request ? "No response from server." : err?.message || fallback;
  if (typeof data === "string" && data.trim()) return data;
  return data?.message || data?.detail || data?.error || err?.message || fallback;
};

export default function useMeetingPoints() {
  const authToken = sessionStorage.getItem("authToken");
  const [meetingPoints, setMeetingPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAxiosErrors = (err, action = "operation") => {
    const message = extractErrorMessage(err, action);
    const normalized = new Error(message);
    normalized.status = err?.response?.status;
    normalized.raw = err;
    setError(normalized);
    toast.error(message, MEETING_POINTS_TOAST_CONFIG);
    return normalized;
  };

  const fetchMeetingPoints = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(MEETING_POINTS_API_BASE_URL);
      setMeetingPoints(normalizeRows(response.data));
    } catch (err) {
      handleAxiosErrors(err, "fetch meeting points");
    } finally {
      setLoading(false);
    }
  }, []);

  const createMeetingPoint = useCallback(
    async (payload) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.post(MEETING_POINTS_API_BASE_URL, payload);
        await fetchMeetingPoints();
        toast.success("Meeting point created successfully.", MEETING_POINTS_TOAST_CONFIG);
        return response.data;
      } catch (err) {
        throw handleAxiosErrors(err, "create meeting point");
      } finally {
        setLoading(false);
      }
    },
    [fetchMeetingPoints],
  );

  const updateMeetingPoint = useCallback(
    async (id, payload) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.put(`${MEETING_POINTS_API_BASE_URL}/${id}`, payload);
        await fetchMeetingPoints();
        toast.success("Meeting point updated successfully.", MEETING_POINTS_TOAST_CONFIG);
        return response.data;
      } catch (err) {
        throw handleAxiosErrors(err, "update meeting point");
      } finally {
        setLoading(false);
      }
    },
    [fetchMeetingPoints],
  );

  const deleteMeetingPoint = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`${MEETING_POINTS_API_BASE_URL}/${id}`);
        await fetchMeetingPoints();
        toast.success("Meeting point deleted successfully.", MEETING_POINTS_TOAST_CONFIG);
      } catch (err) {
        throw handleAxiosErrors(err, "delete meeting point");
      } finally {
        setLoading(false);
      }
    },
    [fetchMeetingPoints],
  );

  useEffect(() => {
    if (authToken) fetchMeetingPoints();
  }, [authToken, fetchMeetingPoints]);

  return {
    meetingPoints,
    loading,
    error,
    fetchMeetingPoints,
    createMeetingPoint,
    updateMeetingPoint,
    deleteMeetingPoint,
  };
}
